import { useEffect, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/hooks/useLanguage";
import { sessionGate, signInPath, type SessionState } from "@/lib/auth/sessionGate";

/**
 * Renders app pages only for a signed-in visitor. While the session check
 * runs the page renders as usual (signed-in users see no flash); once it
 * says there is no session, or the user signs out, it goes to sign-in with
 * the wanted page as `next`. Data is safe either way (RLS); this keeps a
 * signed-out visitor off an empty app shell.
 */
export function RequireSession({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const [state, setState] = useState<SessionState>({ loading: true });

  useEffect(() => {
    let alive = true;
    supabase.auth.getSession().then(({ data, error }) => {
      if (alive) setState({ loading: false, signedIn: !error && !!data.session?.user });
    });
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      if (alive) setState({ loading: false, signedIn: !!session?.user });
    });
    return () => {
      alive = false;
      data.subscription.unsubscribe();
    };
  }, []);

  const gate = sessionGate(state);
  useEffect(() => {
    if (gate === "redirect") {
      navigate(signInPath(language, location.pathname, location.search), { replace: true });
    }
  }, [gate, language, location.pathname, location.search, navigate]);

  return gate === "redirect" ? null : <>{children}</>;
}
